import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useMatches } from 'react-router';
import { type SubscriptionTerm } from '@cx-sdk/subs';
import { SUBSCRIPTION_TERM } from '@cx-sdk/subs/schema';
import { type SerializeFrom, type SubsProduct } from '@cx-web/domain-types';
import { useMyOCUser } from '@cx-web/feature-myoc';
import { isSupportedRepub, REPUB_CONFIG } from '@cx-web/repub-config';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import classNames from 'classnames';
import clsx from 'clsx';

import { REPUBS } from '@cx-modules/constants';

import { trackLandingFreeTrialClick } from '~/apps/product-plus/components/BettingInsights/tracking';
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
  List,
} from '~/apps/product-plus/components/cxy/cxy.ts';
import FAQ from '~/apps/product-plus/components/FAQ';
import { useSubscriptionFlow } from '~/apps/product-plus/features/subscriptions/hooks/use-subscription-flow.ts';
import type { loader as rootLoader } from '~/apps/product-plus/pages/root-loader';

import { AppLinks } from '../components/AppLink';
import { PriceCard } from '../components/PriceCard';
import { ProductCard } from '../components/ProductCard';
import { useSubscriptionRedirect } from '../shared/hooks/useSubscriptionRedirect';
import { getPostLoginSubscribeProduct } from '../shared/utils/getPostLoginSubscribeProduct';
import styles from '../styles/landing-page.module.css';

import { type LoaderData } from './loader.server';

export { meta } from '../meta';

export default function OddscheckerPlusContent() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    availableProducts,
    cancelPayment,
    isLoggedIn,
    isSubscribed,
    monthlyProducts,
    productPrice,
    redirectPath,
    repub,
    requestContext,
    subscriptionTerm,
    verifyPayment,
    yearlyProducts,
  } = useLoaderData<LoaderData>();

  const { ENV, repubPath, resourcesUrl } = rootLoaderData;
  if (!isSupportedRepub(repub)) {
    throw new Error(`Unsupported repub: ${repub}`);
  }
  const { repubIs } = getRepubMetaFromRepub(repub);

  const { myocData } = useMyOCUser();
  const { t } = useTranslation(['label']);

  const plansRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (repub !== REPUBS.GZ.name) return;

    const IFRAME_RESIZER_INIT_DELAY = 100;

    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      window.parentIFrame?.scrollTo(0, 0);
    }, IFRAME_RESIZER_INIT_DELAY);

    return () => clearTimeout(timeoutId);
  }, [repub]);

  const { searchParams, shouldRedirectToPaymentPortal } = useSubscriptionRedirect(isSubscribed, cancelPayment);

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const defaultYearlyProduct = getDefaultProduct(yearlyProducts);

  const subscriptionLinkEnv = ENV?.ENVIRONMENT === 'PROD' ? 'PROD' : 'NON_PROD';

  // Use shared subscription flow hook
  const { handleProductClick, userHasCancelledSubscription } = useSubscriptionFlow(
    {
      cancelPayment,
      isLoggedIn,
      isSubscribed,
      productPrice,
      redirectPath,
      repub,
      repubPath,
      requestContext,
      subscriptionTerm: (subscriptionTerm ?? undefined) as SubscriptionTerm | undefined,
      userId: myocData?.myOCUser?.userID,
      verifyPayment,
    },
    {
      availableProducts,
      getSelectionLabel: product =>
        product.term === SUBSCRIPTION_TERM.MONTHLY ? 'Essential' : ('Premium' as 'Essential' | 'Premium'),
      onPostLoginSubscribe: () =>
        getPostLoginSubscribeProduct(
          shouldRedirectToPaymentPortal,
          searchParams,
          availableProducts,
          defaultMonthlyProduct,
        ),
    },
  );

  const trackTrial = (position: 1 | 2 | 3 | 4) => {
    trackLandingFreeTrialClick({ position, userHasCancelledSubscription });
    return true;
  };

  const repubConfig = REPUB_CONFIG[repub].plus!;
  const faqs = repubConfig.faq;
  const productFeatures = repubConfig?.subscription.premiumFeatures?.map(f => f.title) || [];

  const getImageUrl = (filename: string) => {
    if (repub === REPUBS.WS.name) {
      return filename.replace(/\.([^.]+)$/, '-ws.$1');
    }
    if (repub === REPUBS.GZ.name) {
      return filename.replace(/\.([^.]+)$/, '-gz.$1');
    }
    return filename;
  };

  // Helper to get CTA button text
  const ctaButtonText = userHasCancelledSubscription ? t('CX_SUBSCRIBE_NOW') : t('CX_TRY_FOR_FREE');

  // Helper to get available products for price cards
  const displayProducts =
    repub === REPUBS.GZ.name && yearlyProducts.length === 0
      ? monthlyProducts
      : [defaultMonthlyProduct, defaultYearlyProduct].filter((product): product is SubsProduct => Boolean(product));

  // Helper to scroll to plans section
  const scrollToPlans = (smooth = false) => {
    plansRef.current?.scrollIntoView(smooth ? { behavior: 'smooth' } : undefined);
  };

  // Helper to render price cards
  const renderPriceCard = (product: SubsProduct, testIdPrefix: string) => {
    // Map term to test ID - SUBSCRIPTION_TERM.ANNUAL becomes "annually" to match test expectations
    const isGZPrepaidMonthly =
      repub === REPUBS.GZ.name && product.term === SUBSCRIPTION_TERM.MONTHLY && product.recurring.count > 1;
    const termForTestId =
      product.term === SUBSCRIPTION_TERM.ANNUAL
        ? 'annually'
        : isGZPrepaidMonthly
          ? `${product.recurring.count}-monthly`
          : product.term.toLowerCase();
    const testId = testIdPrefix ? `${testIdPrefix}-${termForTestId}-price` : `${termForTestId}-price`;

    return (
      <PriceCard
        data-testid={testId}
        key={product.id}
        onClick={() =>
          handleProductClick({
            productId: product.productId,
          })
        }
        product={product}
        repub={repub}
        repubIs={repubIs}
        subscriptionLinkEnv={subscriptionLinkEnv}
        userHasCancelledSubscription={userHasCancelledSubscription}
      />
    );
  };

  // Product cards from configuration
  const productCards = repubConfig.landingPage.productCards;

  return (
    <>
      <div className={styles.gradient} />

      <main className={styles.home} data-iframe-size>
        <div className={styles.hero} data-testid="hero-content">
          <div>
            <div className={styles.textContainer}>
              <h1 className={styles.title}>{repubConfig.landingPage.heroTitle}</h1>
              <h2 className={styles.subtitle}>{repubConfig.landingPage.heroSubtitle}</h2>
              <AppLinks repubIs={repubIs} resourcesUrl={resourcesUrl} />
              <div className={styles.heroPriceCardWrapper}>
                {displayProducts.map(product => renderPriceCard(product, 'hero'))}
              </div>
              {repub === REPUBS.GZ.name && (
                <Button
                  className={classNames(styles.tertiaryButton, styles.productButton, styles.heroMobileCta)}
                  onClick={() => {
                    scrollToPlans(true);
                    trackTrial(1);
                  }}
                  size="large"
                  variant="secondary"
                >
                  {ctaButtonText}
                </Button>
              )}
            </div>
          </div>
          <picture className={styles.heroImage}>
            <source media="(min-width: 801px)" srcSet={`${resourcesUrl}/assets/img/plus/${getImageUrl('hero.png')}`} />
            <img
              alt="iPhone displaying Positive Value Bets page"
              src={`${resourcesUrl}/assets/img/plus/${getImageUrl('hero-mobile.png')}`}
            />
          </picture>
        </div>
        {productCards.map(card => {
          const description = card.descriptionKey ? repubConfig.landingPage[card.descriptionKey] : card.descriptionText;
          const shouldScrollSmooth = card.trackingPosition === 1;

          return (
            <ProductCard
              data-testid={card.testId}
              image={{
                alt: card.image.alt,
                position: card.image.position,
                src: `${resourcesUrl}/assets/img/plus/${getImageUrl(card.image.filename)}`,
              }}
              key={card.testId}
              title={card.title}
            >
              <p data-testid={`${card.testId.replace('product-card-', '')}-description`}>{description}</p>
              <List
                classNames={{ wrapper: styles.productList }}
                data-testid={`${card.testId.replace('product-card-', '')}-list`}
                items={card.listItems}
                size="m"
              />
              <AppLinks repubIs={repubIs} resourcesUrl={resourcesUrl} />
              <Button
                className={classNames(styles.tertiaryButton, styles.productButton)}
                onClick={() => {
                  scrollToPlans(shouldScrollSmooth);
                  trackTrial(card.trackingPosition);
                }}
                size="large"
                variant="secondary"
              >
                {ctaButtonText}
              </Button>
            </ProductCard>
          );
        })}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle} id="plans" ref={plansRef}>
            {repubConfig.landingPage.plansTitle}
          </h2>
          <div className={styles.planList} data-testid="plans-list">
            <List direction={repub === REPUBS.GZ.name ? 'column' : 'row'} items={productFeatures} />
          </div>
          <div className={classNames(styles.priceCardWrapper, styles.productCards)}>
            {displayProducts.map(product => renderPriceCard(product, ''))}
          </div>
        </section>
        <FAQ>
          <FAQ.Title>{faqs.title}</FAQ.Title>
          <FAQ.Subtitle>{faqs.subtitle}</FAQ.Subtitle>
          <FAQ.List
            items={faqs.questionsAndAnswers.map(faq => ({
              heading: faq.heading,
              content: faq.content(styles.link, scrollToPlans),
            }))}
          />
        </FAQ>
        {repub !== REPUBS.GZ.name && (
          <Drawer>
            <DrawerTrigger asChild>
              <button className={styles.drawerButton} data-testid="drawer-trigger">
                {userHasCancelledSubscription ? t('CX_SUBSCRIBE_NOW') : t('CX_PLUS_START_7_DAY_TRIAL')}
              </button>
            </DrawerTrigger>
            <DrawerContent className={styles.drawerContent}>
              <div className={styles.pricingSection}>
                {[defaultYearlyProduct, defaultMonthlyProduct].filter(Boolean).map(product => (
                  <DrawerClose asChild key={product.id}>
                    {renderPriceCard(product, 'drawer')}
                  </DrawerClose>
                ))}

                {repub === REPUBS.US.name && (
                  <div className={styles.subscribeOptions}>
                    <p>You can also subscribe via our app!</p>
                    <AppLinks repubIs={repubIs} resourcesUrl={resourcesUrl} />
                  </div>
                )}
              </div>

              <DrawerClose asChild>
                <button className={clsx(styles.drawerButton, styles.close)}>Close</button>
              </DrawerClose>
            </DrawerContent>
          </Drawer>
        )}
      </main>
    </>
  );
}
